import {
  acceptHotItemStore,
  defineItemStore
} from "@editor/items/itemStore.store";

import { mapValues } from "lodash-es";

import { toUid } from "@/helpers/utility/Id";
import { i18n } from "@/i18n";
import { showConfirm } from "@core/ui/alert/alert";

import { useRelationStore } from "@editor/items/relation/relations.store";
import { useIndicatorStore } from "@editor/items/indicator/indicators.store";
import { useMomentumStore } from "@editor/items/momentum/momentums.store";
import { useStakeholderStore } from "@editor/items/stakeholder/stakeholders.store";
import { useEffectStore } from "@editor/items/effect/effects.store";
import { useConditionStore } from "@editor/items/condition/conditions.store";
import { useWayStore } from "@editor/items/way/ways.store";
import { useScenarioStore } from "@editor/items/scenario/scenarios.store";
import { useSimulationStore } from "@editor/items/simulation/simulations.store";
import { useFlowBranchStore } from "../flow/flowBranches.store";
import { useSceneNodesStore } from "../scene/sceneNodes.store";

import { changeElementType } from "../element/elementFunctions";

import type { TargetItem } from "./TargetItem";
import type { ResourceItem } from "../resource/ResourceItem";
import type { IndexItem } from "../index/IndexItem";
import type { ExpressionValue } from "../expression/expressionFunctions";

export const defaultTargetStructure: TargetItem = {
  type: "target",
  id: 0,
  uid: "target_0",

  title: "",
  description: "",
  value: 0,
  unit: "%",

  minValue: 0,
  maxValue: 100,

  momentumActive: false,
  // controllable: false,

  docs: {},
  createdAt: -1
};

export const useTargetStore = defineItemStore(
  "model/targets",
  defaultTargetStructure,
  {
    state: () => ({
      minWidth: 200
    }),
    getters: {
      /**
       * The titles of all targets
       */
      titles(state): { [targetId: number]: string } {
        return mapValues(state.items, (e) => e.title);
      },

      /**
       * The values of all targets
       */
      values(state): { [targetId: number]: number } {
        return mapValues(state.items, (e) => e.value);
      },

      expressionValues(state): { [targetUid: string]: ExpressionValue } {
        const values: { [targetUid: string]: ExpressionValue } = {};
        for (const targetId in state.items) {
          const target = state.items[targetId];
          values[target.uid] = {
            value: target.value,
            minValue: 0,
            maxValue: 100,
            unit: target.unit,
            unitValid: true,
            valid: true
          };
        }
        return values;
      }
    },
    events: {
      async canBeDeleted(target) {
        const inSimulations = useSimulationStore().orderedItems.some((simulation) => {
          const elementIds = Object.keys(simulation.startElementValues);
          return elementIds.includes(target.uid);
        });
        if (!inSimulations) return true;
        return await showConfirm(
          i18n.t("questions.deleteTarget"),
          i18n.t("actions.deleteTarget"),
          i18n.t("labels.abort")
        );
      },
      async afterDelete(target): Promise<unknown> {
        const uid = toUid("target", target.id);
        const promises = [];

        // delete all owned INDICATORS
        const indicatorStore = useIndicatorStore();
        const indicators = indicatorStore.orderedItems.filter(
          (indicator) => indicator.targetId === target.id
        );
        if (indicators != null) {
          for (const indicator of indicators) {
            promises.push(
              indicatorStore.delete({ id: indicator.id, instant: true })
            );
          }
        }

        // delete the owned MOMENTUM
        const momentumStore = useMomentumStore();
        const momentum = momentumStore.byTarget[target.id];
        if (momentum != null) {
          promises.push(
            momentumStore.delete({ id: momentum.id, instant: true })
          );
        }

        // delete all connected RELATIONS
        const relationStore = useRelationStore();
        const connectedRelations = relationStore.orderedItems.filter(
          (relation) =>
            (relation.startItemType === "target" &&
              relation.startItemId === target.id) ||
            (relation.endItemId === target.id &&
              relation.endItemType === "target")
        );
        if (connectedRelations != null) {
          for (const relation of connectedRelations) {
            promises.push(
              relationStore.delete({ id: relation.id, instant: true })
            );
          }
        }

        // delete all EFFECTS associated with the target
        const effectStore = useEffectStore();
        const connectedEffects = effectStore.byTarget[target.id];
        if (connectedEffects != null) {
          for (const effect of connectedEffects) {
            promises.push(effectStore.delete({ id: effect.id, instant: true }));
          }
        }

        // remove all CONDITIONS based on this target
        const conditionStore = useConditionStore();
        const conditions = conditionStore.getByActivator({
          type: "target",
          id: target.id
        });
        for (const conditionId in conditions) {
          const condition = conditions[conditionId];
          promises.push(
            conditionStore.delete({ id: condition.id, instant: true })
          );
        }

        // WAYS are deleted by deleting the associated relations
        // by here, when they only have a start target
        const wayStore = useWayStore();
        const ways = wayStore.byStartItem[target.id];
        if (ways != null) {
          for (const way of ways) {
            promises.push(wayStore.delete({ id: way.id, instant: true }));
          }
        }

        // remove start values for this target from all SCENARIOS
        const scenarioStore = useScenarioStore();
        const scenarios = scenarioStore.orderedItems;
        if (scenarios != null) {
          for (const scenario of scenarios) {
            if (scenario.targetValues[target.id] != null) {
              promises.push(
                scenarioStore.deleteTargetValue({
                  id: scenario.id,
                  targetId: target.id
                })
              );
            }
          }
        }

        // remove start values for this target from all SIMULATIONS
        const simulationStore = useSimulationStore();
        const simulations = simulationStore.items;
        if (simulations != null) {
          for (const simulationId in simulations) {
            const simulation = simulations[simulationId];
            if (simulation.startElementValues[uid] != null) {
              promises.push(
                simulationStore.deleteTargetValue({
                  id: simulation.id,
                  targetId: target.id
                })
              );
            }
          }
        }

        // remove connections from stakeholders
        const stakeholderStore = useStakeholderStore();
        const stakeholders = stakeholderStore.byTarget[target.id] ?? [];
        for (const stakeholder of stakeholders) {
          stakeholderStore.removeTarget({
            id: stakeholder.id,
            targetId: target.id
          });
        }

        // remove all connected flow branches
        const flowBranchStore = useFlowBranchStore();
        const flowBranches = flowBranchStore.byElementUid[uid] ?? [];
        for (const branch of flowBranches) {
          promises.push(
            flowBranchStore.delete({ id: branch.id, instant: true })
          );
        }

        const sceneNodeStore = useSceneNodesStore();
        const nodes = sceneNodeStore.byNodeType.element ?? [];
        const targetNodes = nodes.filter((node) => node.data.elementUid === uid);
        for (const node of targetNodes) {
          promises.push(
            sceneNodeStore.delete({ id: node.id, instant: true })
          );
        }

        return Promise.all(promises);

        // end of deletion event
      }
    },
    actions: {
      async changeType(id: number, type: "index" | "resource"): Promise<ResourceItem | IndexItem | undefined> {
        const target = this.items[id];
        if (target == null) return;

        const newElement = await changeElementType(target, type);

        await this.delete({ id: target.id, instant: true });
        return newElement;
      }
    }
  }
);

if (import.meta.hot) {
  import.meta.hot.accept(acceptHotItemStore(useTargetStore, import.meta.hot));
}
